import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  MoreVert,
  Person,
  CalendarToday,
  AttachMoney,
  Home,
  CheckCircle,
  PendingActions,
  Group,
  Language,
  Business,
} from '@mui/icons-material';
import { Booking, CalendarBooking } from '@/types';
import { BookingsService } from '@/utils/bookings-service';
import { BookingWithGuestStatus } from '@/hooks/api/useGuests';
import { BookingConfirmationStatus } from './BookingConfirmationStatus';
import { getBookingSourceText, getBookingSourceIcon } from '@/utils/calendar-utils';

interface BookingCardProps {
  booking: Booking | BookingWithGuestStatus | CalendarBooking;
  listingTitle?: string;
  onEdit?: (booking: Booking | BookingWithGuestStatus | CalendarBooking) => void;
  onCancel?: (booking: Booking | BookingWithGuestStatus | CalendarBooking) => void;
  onStatusChange?: (booking: Booking | BookingWithGuestStatus | CalendarBooking, status: string) => void;
  onClick?: (booking: Booking | BookingWithGuestStatus | CalendarBooking) => void;
  showGuestStatus?: boolean;
  showSource?: boolean;
}

export const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  listingTitle,
  onEdit,
  onCancel,
  onStatusChange,
  onClick,
  showGuestStatus = false,
  showSource = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event?: React.MouseEvent) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
  };

  const handleMenuCloseFromMUI = () => {
    setAnchorEl(null);
  };

  const handleEdit = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleMenuClose();
    onEdit?.(booking);
  };

  const handleCancel = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleMenuClose();
    onCancel?.(booking);
  };



  const handleStatusChange = (event: React.MouseEvent, status: string) => {
    event.stopPropagation();
    handleMenuClose();
    onStatusChange?.(booking, status);
  };

  const duration = BookingsService.calculateDuration(booking.startDate, booking.endDate);
  const dateRange = BookingsService.formatDateRange(booking.startDate, booking.endDate);
  const statusColor = BookingsService.getStatusColor(booking.status);

  // Check if this is a booking with guest status
  const bookingWithStatus = booking as BookingWithGuestStatus;
  const hasGuestStatus = showGuestStatus && bookingWithStatus.guestCompletionStatus;
  
  // Check if this is a calendar booking with source info
  const calendarBooking = booking as CalendarBooking;
  const hasSourceInfo = showSource && calendarBooking.isExternal !== undefined;

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger card click if clicking on menu button
    if ((e.target as HTMLElement).closest('[data-testid="more-menu"]')) {
      return;
    }
    onClick?.(booking);
  };

  // Calculate guest completion percentage
  const getCompletionPercentage = () => {
    if (!hasGuestStatus) return 0;
    const { completed, total } = bookingWithStatus.guestCompletionStatus;
    return total > 0 ? (completed / total) * 100 : 0;
  };

  // Get guest status color
  const getGuestStatusColor = () => {
    if (!hasGuestStatus) return 'default';
    const { completed, total } = bookingWithStatus.guestCompletionStatus;
    if (completed === total) return 'success';
    if (completed === 0) return 'error';
    return 'warning';
  };

  return (
    <Card 
      sx={{ 
        mb: 2, 
        '&:hover': { boxShadow: 3 },
        cursor: onClick ? 'pointer' : 'default'
      }}
      onClick={handleCardClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="h6" component="h3">
                Booking #{booking.bookingId.slice(-8)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Guest ID: {booking.guestId}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              color={statusColor}
              size="small"
            />
            <IconButton 
              onClick={handleMenuClick} 
              size="small"
              data-testid="more-menu"
            >
              <MoreVert />
            </IconButton>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {listingTitle && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Home fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>Property:</strong> {listingTitle}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarToday fontSize="small" color="action" />
            <Typography variant="body2">
              <strong>Dates:</strong> {dateRange} ({duration} night{duration !== 1 ? 's' : ''})
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person fontSize="small" color="action" />
            <Typography variant="body2">
              <strong>Guests:</strong> {Array.isArray(booking.guests) ? booking.guests.length : booking.guests}
            </Typography>
          </Box>

          {hasGuestStatus && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Group fontSize="small" color="action" />
                <Typography variant="body2">
                  <strong>Check-in Status:</strong>
                </Typography>
                <Chip
                  label={`${bookingWithStatus.guestCompletionStatus.completed}/${bookingWithStatus.guestCompletionStatus.total} completed`}
                  size="small"
                  color={getGuestStatusColor()}
                  icon={
                    bookingWithStatus.guestCompletionStatus.completed === bookingWithStatus.guestCompletionStatus.total 
                      ? <CheckCircle fontSize="small" />
                      : <PendingActions fontSize="small" />
                  }
                />
              </Box>
              
              {bookingWithStatus.guestCompletionStatus.total > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={getCompletionPercentage()}
                    sx={{ 
                      flexGrow: 1, 
                      height: 6, 
                      borderRadius: 3,
                      backgroundColor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getGuestStatusColor() === 'success' ? 'success.main' : 
                                       getGuestStatusColor() === 'warning' ? 'warning.main' : 'error.main'
                      }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {Math.round(getCompletionPercentage())}%
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoney fontSize="small" color="action" />
            <Typography variant="body2">
              <strong>Total:</strong> ${booking.totalPrice.toFixed(2)}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Payment:</strong> 
              <Chip
                label={booking.paymentStatus.charAt(0).toUpperCase() + booking.paymentStatus.slice(1)}
                size="small"
                color={booking.paymentStatus === 'paid' ? 'success' : booking.paymentStatus === 'refunded' ? 'error' : 'warning'}
                sx={{ ml: 1 }}
              />
            </Typography>
          </Box>

          {hasSourceInfo && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {calendarBooking.isExternal ? <Language fontSize="small" color="action" /> : <Business fontSize="small" color="action" />}
              <Typography variant="body2">
                <strong>Source:</strong> {getBookingSourceText(calendarBooking)}
                {calendarBooking.isExternal && (
                  <Chip
                    label="External"
                    size="small"
                    color="secondary"
                    sx={{ ml: 1 }}
                    icon={<Language fontSize="small" />}
                  />
                )}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Confirmations:</strong>
            </Typography>
            <BookingConfirmationStatus bookingId={booking.bookingId} compact />
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Created: {new Date(booking.createdAt).toLocaleDateString()}
          {booking.status === 'cancelled' && booking.cancelledAt && (
            <span style={{ marginLeft: '16px' }}>
              • Cancelled: {new Date(booking.cancelledAt).toLocaleDateString()}
            </span>
          )}
        </Typography>
      </CardContent>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuCloseFromMUI}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {/* Only show edit/delete options for direct bookings */}
        {!hasSourceInfo || !calendarBooking.isExternal ? (
          <>
            {booking.status === 'pending' && (
              <MenuItem onClick={(e) => handleStatusChange(e, 'confirmed')}>
                Confirm Booking
              </MenuItem>
            )}
            {booking.paymentStatus === 'pending' && booking.status === 'confirmed' && (
              <MenuItem onClick={(e) => handleStatusChange(e, 'payment-confirmed')}>
                Confirm Payment
              </MenuItem>
            )}
            {booking.status === 'confirmed' && (
              <MenuItem onClick={(e) => handleStatusChange(e, 'completed')}>
                Mark as Completed
              </MenuItem>
            )}
            {(booking.status === 'pending' || booking.status === 'confirmed') && (
              <MenuItem onClick={handleCancel}>
                Cancel Booking
              </MenuItem>
            )}
            <MenuItem onClick={handleEdit}>
              Edit Details
            </MenuItem>
          </>
        ) : (
          <>
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                External bookings cannot be edited
              </Typography>
            </MenuItem>
            <MenuItem onClick={(e) => handleMenuClose(e)}>
              View Details Only
            </MenuItem>
          </>
        )}
      </Menu>
    </Card>
  );
};

export default BookingCard;